'use client';

import styled from '@emotion/styled';
import { useCallback, useEffect, useState } from 'react';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ButtonSize, ButtonType } from 'constants/button';
import { ModalParams, ModalType } from 'constants/modal';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';
import { isPC } from '@/constants/layout';

export const AlertModal = () => {
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.Alert]>();

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    hideModal();
  }, [hideModal, state]);

  const handleConfirm = useCallback(() => {
    state?.onConfirm?.();
    hideModal();
  }, [hideModal, state]);

  useEffect(() => {
    const handleBeforePopState = () => {
      if (isShow) {
        handleClose();
      }
    };

    window.addEventListener('popstate', handleBeforePopState);

    return () => {
      window.removeEventListener('popstate', handleBeforePopState);
    };
  }, [handleClose, isShow]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.Alert]) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
      setState(params);
    };

    ModalHandler.addEventListener(ModalType.Alert, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.Alert);
    };
  }, [hideModal, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal}>
      <StyledModal>
        <StyledTitle hasDescription={!!state?.description}>{state?.title}</StyledTitle>
        {state?.description && <StyledDescription>{state?.description}</StyledDescription>}
        <StyledButtons>
          {state?.cancelText && (
            <StyledButton
              label={state?.cancelText}
              size={ButtonSize.Small}
              buttonTypography={Typography.XXXSmall}
              buttonType={ButtonType.SecondaryNoBorder}
              onClick={handleClose}
            />
          )}
          <StyledButton
            label={state?.confirmText || '확인'}
            size={ButtonSize.Small}
            buttonTypography={Typography.XXXSmall}
            onClick={handleConfirm}
          />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: calc(100vw - 60px);
  max-width: 320px;
  background-color: ${Color.White};
  padding: 24px;
  border-radius: 4px;
  box-shadow:
    0 4px 8px -3px rgba(0, 0, 0, 0.2),
    0px 16px 32px -3px rgba(0, 0, 0, 0.2);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (${isPC}) {
    max-width: 400px;
  }
`;

const StyledTitle = styled.p<{ hasDescription: boolean }>`
  margin-bottom: ${({ hasDescription }) => (hasDescription ? '16px' : '24px')};
  text-align: left;
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  word-break: break-all;
  white-space: pre-line;
`;

const StyledDescription = styled.p`
  color: ${Color.Gray700};
  text-align: left;
  margin-bottom: 24px;
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  word-break: break-all;
  white-space: pre-line;
`;

const StyledButton = styled(Button)`
  padding: 0 20px;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 40px;
`;
